import { Injectable } from '@angular/core';
import { SessionAnalyticsService } from './session-analytics.service';

@Injectable({
  providedIn: 'root',
})
export class TrackerService {
  private globalDataWithSteps: any;
  private sessionData: any = {};
  private orderId: string = 'Unknown';
  private gatewayRecommendation: string = 'Unknown';
  private transactionId: string = 'Unknown';
  private cryptoTransactionStatus: string = '';
  private isCrypto: boolean = false;
  private cryptoCurrency: string = 'Unknown';

  constructor(private sessionAnalyticsService: SessionAnalyticsService) {
    this.globalDataWithSteps = this.sessionAnalyticsService.trackEventsFromGlobaleData();
    this.initializeSessionData();
  }

  private initializeSessionData(): void {
    this.sessionData = this.sessionAnalyticsService.trackEventsFromGlobaleData() || {};
    
    if (!this.sessionData.google_analytics_steps) {
      this.sessionData.google_analytics_steps = {};
    }
    
    const orderIdFromGlobal = this.globalDataWithSteps?.order_id;
    const transactionIdFromGlobal = this.globalDataWithSteps?.transaction_id;
    
    this.orderId = orderIdFromGlobal || 'Unknown';
    this.gatewayRecommendation = this.globalDataWithSteps?.gatewayRecommendation || 'Unknown';
    this.transactionId = transactionIdFromGlobal || 'Unknown';
    this.cryptoTransactionStatus = this.globalDataWithSteps?.transaction_status_crypto || '';
    this.isCrypto = this.globalDataWithSteps?.isCrypto || false;
    this.cryptoCurrency = this.globalDataWithSteps?.crypto_currency || this.globalDataWithSteps?.currency || 'Unknown';
    
    this.sessionData.order_id = this.orderId;
    this.sessionData.gatewayRecommendation = this.gatewayRecommendation;
    this.sessionData.transaction_id = this.transactionId;
    
    if (this.isCrypto && this.cryptoTransactionStatus) {
      this.sessionData.transaction_status_crypto = this.cryptoTransactionStatus;
      this.sessionData.isCrypto = true;
      this.sessionData.crypto_currency = this.cryptoCurrency;
    }
  }

  public setOrderId(orderId: string): void {
    if (orderId && orderId !== 'Unknown') {
      this.orderId = orderId;
      this.sessionData.order_id = orderId;
    }
  }

  public setGatewayRecommendation(gatewayRecommendation: string): void {
    if (gatewayRecommendation && gatewayRecommendation !== 'Unknown') {
      this.gatewayRecommendation = gatewayRecommendation;
      this.sessionData.gatewayRecommendation = gatewayRecommendation;
    }
  }
  
  public setTransactionId(transactionId: string): void {
    if (transactionId && transactionId !== 'Unknown') {
      this.transactionId = transactionId;
      this.sessionData.transaction_id = transactionId;
    }
  }
  
  public setCryptoCurrency(cryptoCurrency: string): void {
    if (cryptoCurrency && cryptoCurrency !== 'Unknown') {
      this.cryptoCurrency = cryptoCurrency;
      this.sessionData.crypto_currency = cryptoCurrency;
    }
  }
  
  public setCryptoTransactionStatus(status: string, isCrypto: boolean = true): void {
    if (status) {
      this.cryptoTransactionStatus = status;
      this.isCrypto = isCrypto;
      this.sessionData.transaction_status_crypto = status;
      this.sessionData.isCrypto = isCrypto;
      
      console.log(`Setting crypto transaction status in TrackerService: ${status}`);
      
      this.updateGlobalData({
        transaction_status_crypto: status,
        ga_transaction_status_crypto: status,
        isCrypto: isCrypto
      });
      
      this.sessionAnalyticsService.trackEvent(
        'transaction_status_crypto',
        'transaction',
        status,
        1,
        {
          transaction_status: status,
          transaction_status_crypto: status,
          ga_transaction_status_crypto: status,
          order_id: this.orderId,
          transaction_id: this.transactionId,
          crypto_currency: this.cryptoCurrency,
          gatewayRecommendation: this.gatewayRecommendation,
          is_confirmed_page: true,
          isCrypto: isCrypto
        },
        10
      );
    }
  }

  private updateGlobalData(additionalData: Record<string, any> = {}): void {
    if (this.isCrypto) {
      const globalAnalyticsData = {
        ...this.globalDataWithSteps,
        transaction_status_crypto: this.cryptoTransactionStatus,
        ga_transaction_status_crypto: this.cryptoTransactionStatus,
        transaction_id: this.transactionId,
        crypto_currency: this.cryptoCurrency,
        order_id: this.orderId,
        gatewayRecommendation: this.gatewayRecommendation,
        isCrypto: true,
        ...additionalData
      };
      
      this.sessionAnalyticsService.trackEvent(
        'global_data',
        'user_journey',
        `Crypto Data Update: ${this.cryptoTransactionStatus}`,
        1,
        globalAnalyticsData,
        5
      );
    }
  }

  private getEventData(): any {
    const eventData: any = {
      order_id: this.sessionData.order_id,
      gatewayRecommendation: this.sessionData.gatewayRecommendation,
      transaction_id: this.sessionData.transaction_id,
      ga_order_id: this.sessionData.order_id,
      ga_gatewayRecommendation: this.sessionData.gatewayRecommendation,
      ga_transaction_id: this.sessionData.transaction_id,
      event_category: 'user_journey',
      value: 1
    };
    
    if (this.isCrypto && this.cryptoTransactionStatus) {
      eventData.transaction_status = this.cryptoTransactionStatus;
      // eventData.transaction_status_crypto = this.cryptoTransactionStatus;
      // eventData.ga_transaction_status_crypto = this.cryptoTransactionStatus;
      eventData.crypto_currency = this.cryptoCurrency;
      eventData.isCrypto = true;
    }
    
    return eventData;
  }


  public trackStep1(): void {
    if (!this.sessionData?.google_analytics_steps?.step_1) {
      const stepDescription = `Session started | Order ID: ${this.sessionData.order_id}`;
      
      this.sessionData.google_analytics_steps = {
        ...this.sessionData.google_analytics_steps,
        step_1: stepDescription
      };
  
      const eventData = this.getEventData();
      eventData.event_label = 'Step 1 - Session started';
  
      this.sessionAnalyticsService.trackStepsFromSession(
        this.globalDataWithSteps,
        eventData,
        'step_1'
      );
    }
  }


  public trackStep2(): void {
    if (!this.sessionData?.google_analytics_steps?.step_2) {
      const stepDescription = `Sensitive data entered | Order ID: ${this.sessionData.order_id} | Gateway: ${this.sessionData.gatewayRecommendation}`;
      
      this.sessionData.google_analytics_steps = {
        ...this.sessionData.google_analytics_steps,
        step_2: stepDescription
      };
  
      const eventData = this.getEventData();
      eventData.event_label = 'Step 2 - Sensitive data entered';
  
      this.sessionAnalyticsService.trackStepsFromSession(
        this.globalDataWithSteps,
        eventData,
        'step_2'
      );
    }
  }

  public trackStep3(): void {
    if (!this.sessionData?.google_analytics_steps?.step_3) {
      const stepDescription = `External link started | Order ID: ${this.sessionData.order_id} | Gateway: ${this.sessionData.gatewayRecommendation}`;
      
      this.sessionData.google_analytics_steps = {
        ...this.sessionData.google_analytics_steps,
        step_3: stepDescription
      };
  
      const eventData = this.getEventData();
      eventData.event_label = 'Step 3 - External link started';
  
      this.sessionAnalyticsService.trackStepsFromSession(
        this.globalDataWithSteps,
        eventData,
        'step_3'
      );
    }
  }
  
  public trackStep4(): void {
    if (!this.sessionData?.google_analytics_steps?.step_4) {
      const stepDescription = `External link ended | Order ID: ${this.sessionData.order_id} | Gateway: ${this.sessionData.gatewayRecommendation}`;
      
      this.sessionData.google_analytics_steps = {
        ...this.sessionData.google_analytics_steps,
        step_4: stepDescription
      };
  
      const eventData = this.getEventData();
      eventData.event_label = 'Step 4 - External link ended';
  
      this.sessionAnalyticsService.trackStepsFromSession(
        this.globalDataWithSteps,
        eventData,
        'step_4'
      );
    }
  }

  public trackStep5(): void {
    if (!this.sessionData?.google_analytics_steps?.step_5) {
      let stepDescription = `Response received & displayed | Order ID: ${this.sessionData.order_id} | Gateway: ${this.sessionData.gatewayRecommendation}`;
      
      if (this.isCrypto && this.cryptoTransactionStatus) {
        stepDescription += ` | Crypto Status: ${this.cryptoTransactionStatus}`;
      }
      
      this.sessionData.google_analytics_steps = {
        ...this.sessionData.google_analytics_steps,
        step_5: stepDescription
      };

      const eventData = this.getEventData();
      eventData.event_label = 'Step 5 - Response received & displayed';

      this.sessionAnalyticsService.trackStepsFromSession(
        this.globalDataWithSteps,
        eventData,
        'step_5'
      );
    }
  }

  public trackStep5_ApplePay(): void {
    if (!this.sessionData?.google_analytics_steps?.step_5) {
      let stepDescription = `Response received | Order ID: ${this.sessionData.order_id} | Gateway: ${this.sessionData.gatewayRecommendation}`;
      
      if (this.isCrypto && this.cryptoTransactionStatus) {
        stepDescription += ` | Crypto Status: ${this.cryptoTransactionStatus}`;
      }
      
      this.sessionData.google_analytics_steps = {
        ...this.sessionData.google_analytics_steps,
        step_5: stepDescription
      };

      const eventData = this.getEventData();
      eventData.event_label = 'Step 5 - Response received';

      this.sessionAnalyticsService.trackStepsFromSession(
        this.globalDataWithSteps,
        eventData,
        'step_5'
      );
    }
  }

  public trackStep6(): void {
    if (!this.sessionData?.google_analytics_steps?.step_6) {
      let stepDescription = `Session ended | Order ID: ${this.sessionData.order_id} | Gateway: ${this.sessionData.gatewayRecommendation}`;
      if (this.isCrypto && this.cryptoTransactionStatus) {
        stepDescription += ` | Crypto Status: ${this.cryptoTransactionStatus}`;
      }
      
      this.sessionData.google_analytics_steps = {
        ...this.sessionData.google_analytics_steps,
        step_6: stepDescription
      };

      const eventData = this.getEventData();
      eventData.event_label = 'Step 6 - Session ended';

      this.sessionAnalyticsService.trackStepsFromSession(
        this.globalDataWithSteps,
        eventData,
        'step_6'
      );
    }
  }
}